import { defineStore } from "pinia"
import { apiV1 } from "@/config/axios"

export const useTwoFactorStore = defineStore("twoFactor", {
  state: () => ({
    isEnabled: false,
    isConfirmed: false,
    qrCodeSvg: null,
    recoveryCodes: [],
    recoveryCodesCount: 0,
    loading: false
  }),
  getters: {
    is2FAEnabled: (state) => state.isEnabled && state.isConfirmed,
    hasRecoveryCodes: (state) => state.recoveryCodes.length > 0
  },
  actions: {
    async getSettings() {
      this.loading = true
      try {
        const { data } = await apiV1.get("/users/two-factor/settings")
        this.isEnabled = data.enabled
        this.isConfirmed = data.confirmed
        this.recoveryCodesCount = data.recovery_codes_count
        return data
      } finally {
        this.loading = false
      }
    },

    async enable2FA(password) {
      this.loading = true
      try {
        const { data } = await apiV1.post("/users/two-factor/enable", {
          password
        })
        this.isEnabled = data.enabled
        this.isConfirmed = data.confirmed
        this.qrCodeSvg = data.qr_code
        this.recoveryCodes = data.recovery_codes
        return data
      } finally {
        this.loading = false
      }
    },

    async disable2FA(password) {
      this.loading = true
      try {
        const { data } = await apiV1.delete("/users/two-factor/disable", {
          data: { password }
        })
        this.isEnabled = data.enabled
        this.isConfirmed = data.confirmed
        this.qrCodeSvg = null
        this.recoveryCodes = []
        this.recoveryCodesCount = 0
        return data
      } finally {
        this.loading = false
      }
    },

    async getQrCode() {
      this.loading = true
      try {
        const { data } = await apiV1.get("/users/two-factor/qr-code")
        this.qrCodeSvg = data.svg
        return data
      } finally {
        this.loading = false
      }
    },

    async getSetupKey() {
      const { data } = await apiV1.get("/users/two-factor/setup-key")
      return data
    },

    async verifyPassword(password) {
      const { data } = await apiV1.post(
        "/users/two-factor/verify-password",
        { password }
      )
      return data
    },

    async confirm2FA(code) {
      this.loading = true
      try {
        const { data } = await apiV1.post("/users/two-factor/confirm", {
          code
        })
        this.isConfirmed = data.confirmed
        return data
      } finally {
        this.loading = false
      }
    },

    async getRecoveryCodes() {
      this.loading = true
      try {
        const { data } = await apiV1.get("/users/two-factor/recovery-codes")
        this.recoveryCodes = data.recovery_codes
        this.recoveryCodesCount = data.count
        return data.recovery_codes
      } finally {
        this.loading = false
      }
    },

    async regenerateRecoveryCodes(password) {
      this.loading = true
      try {
        const { data } = await apiV1.post(
          "/users/two-factor/recovery-codes",
          { password }
        )
        this.recoveryCodes = data.recovery_codes
        this.recoveryCodesCount = data.recovery_codes.length
        return data.recovery_codes
      } finally {
        this.loading = false
      }
    },

    async verifyChallenge(code, recoveryCode = null) {
      this.loading = true
      try {
        const payload = recoveryCode
          ? { recovery_code: recoveryCode }
          : { code }
        const { data } = await apiV1.post("/two-factor-challenge", payload)
        return data
      } finally {
        this.loading = false
      }
    },

    setUserTwoFactorStatus(user) {
      this.isEnabled = user?.two_factor_enabled || false
      this.isConfirmed = user?.two_factor_confirmed_at !== null
    },

    resetState() {
      this.isEnabled = false
      this.isConfirmed = false
      this.qrCodeSvg = null
      this.secretKey = null
      this.recoveryCodes = []
      this.passwordConfirmed = false
      this.passwordConfirmedAt = null
    }
  }
})
